import type { CSSProperties } from 'react';
import type { FieldDefinition, ValidationIssue } from '@inside-us-aos/domain';

import styles from './TextInput.module.css';
import { issueMessage } from './issueMessage';

/**
 * Canonical primitive prop signature for Phase 2 catalog-driven inputs.
 * Every primitive in `@inside-us-aos/ui` follows this shape: it receives
 * the catalog `field` definition, the current `value`, the per-field
 * `issues` from the form engine, and an `onChange` handler. The form
 * engine — not the primitive — is the source of truth for validity.
 */
export interface TextInputProps {
  field: FieldDefinition;
  value: string;
  issues: readonly ValidationIssue[];
  onChange: (next: string) => void;
  /**
   * Whether the user has interacted with the field. When `false`, the
   * primitive suppresses validation issues so the form doesn't show
   * red errors before the user has had a chance to answer. When
   * `true` (or `undefined`, the default for backward compatibility),
   * issues render as today.
   *
   * The applicant-web shell flips this to `true` on the first
   * `onChange` / `onBlur` for the field, or when the user clicks
   * "Mark section complete" / "Submit" so missing-required errors
   * surface en masse on a deliberate submit attempt.
   */
  touched?: boolean;
  /**
   * Fires on the input's native `onBlur`. The applicant-web shell
   * uses this to mark the field touched the moment focus leaves it.
   * Optional so existing consumers don't need to wire it.
   */
  onBlur?: () => void;
}

/**
 * Minimal text input bound to a `FieldDefinition`. Renders the field's
 * `label`, an `<input type="text">`, and a flat list of validation
 * issues below. ARIA wires `aria-invalid` and `aria-describedby` to the
 * issue list when issues exist.
 *
 * Styling is owned by `TextInput.module.css` (CSS modules per CLAUDE.md
 * BEM-like convention). Phase 2 batch 2 replaces the inline styles
 * shipped in batch 1 so every primitive can adopt the same pattern.
 */
export function TextInput({
  field,
  value,
  issues,
  onChange,
  touched = true,
  onBlur,
}: TextInputProps): JSX.Element {
  const inputId = `field-${field.id.replace(/\./g, '-')}`;
  const errorListId = `${inputId}-errors`;
  const showIssues = touched && issues.length > 0;
  const inputClassName = showIssues
    ? `${styles.field__input} ${styles['field__input--invalid']}`
    : styles.field__input;

  // Wire the catalog's `max_length` rule to the native maxLength
  // attribute so the browser stops the user at the USCIS character
  // budget instead of letting them type past it. The visible cap text
  // ("Up to N characters") used to be auto-appended here — removed
  // because it leaked PDF-widget bookkeeping into user-facing copy.
  // The native attribute still enforces the cap silently; intentional
  // hints belong in the field's `helpText`.
  const maxLengthRule = field.validation?.find(
    (rule): rule is { type: 'max_length'; value: number } =>
      rule.type === 'max_length',
  );
  const renderedHelp = field.helpText;
  // 2026-05-26 — content-sized cap applies only to truly short
  // fixed-format fields: A-Number (9), USCIS account (~12),
  // unit_number (6), ZIP (5), phones (digits-only stored). The
  // earlier threshold of ≤ 32 chars dragged street (25) and city
  // (20) into the cap too, which read as awkwardly narrow at full
  // form width. Streets and cities now stretch to the AddressComposite
  // grid cell's width — the cell span (set by AddressComposite per
  // field semantic) drives the visible width instead. Above 12
  // chars the field is treated as free-form and inherits the cell
  // width.
  const inputCapStyle: CSSProperties | undefined =
    typeof maxLengthRule?.value === 'number' && maxLengthRule.value <= 12
      ? ({ '--field-input-max-ch': `${maxLengthRule.value + 4}ch` } as CSSProperties)
      : undefined;

  return (
    <div className={styles.field} data-field-id={field.id}>
      <label htmlFor={inputId} className={styles.field__label}>
        {field.label}
        {field.required === true ? (
          <span className={styles['field__required-mark']}>*</span>
        ) : null}
      </label>

      <input
        id={inputId}
        type="text"
        value={value}
        onChange={event => onChange(event.target.value)}
        onBlur={onBlur}
        aria-required={field.required === true || undefined}
        aria-invalid={showIssues || undefined}
        aria-describedby={showIssues ? errorListId : undefined}
        maxLength={maxLengthRule?.value}
        className={inputClassName}
        style={inputCapStyle}
      />

      {renderedHelp !== undefined && renderedHelp !== '' ? (
        <p className={styles['field__help-text']}>{renderedHelp}</p>
      ) : null}

      {showIssues ? (
        <div role="alert">
          {/* role="alert" lives on a wrapper, not the <ul>, so the
              list keeps its implicit list semantics (axe: 'listitem'
              rule). Both work together: AT announces the alert and
              the list structure stays intact. */}
          <ul id={errorListId} className={styles['field__error-list']}>
            {issues.map((issue, index) => (
              <li key={`${issue.code}-${index}`}>
                <span
                  aria-hidden="true"
                  className={styles['field__error-icon']}
                >
                  !
                </span>
                {issueMessage(issue)}
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </div>
  );
}
